import { IThemeConfig } from '@trimble-oss/moduswebcomponents';
import { makePersisted } from '@solid-primitives/storage';
import { makeMergedPersisted } from '@trimble/solidjs-utils';
import { Web3dSettings } from '@trimble/web3d-capabilities';
import {
  Accessor,
  createContext,
  createEffect,
  createSignal,
  JSX,
  Setter,
  useContext,
} from 'solid-js';
import { PERSISTENCE_KEY } from './constants/make-persisted-constants';
import { getWeb3dSettingsForTheme } from './constants/theme-constants';
import {
  DEFAULT_POINT_BUDGET,
  isValidPointBudget,
  parsePointBudget,
} from './constants/point-budget-constants';
import {
  DEFAULT_VIEWER_PREFERENCE_SETTINGS,
  isValidViewerPreferenceSettings,
  ViewerPreferenceSettings,
} from '@trimble/viewer-preferences';
import { Theme } from '@trimble/page-header';

type ThemeSettings = {
  darkModeMaterials: boolean;
  backgroundTopColor: string;
  backgroundBottomColor: string;
  textColor: string;
};

type ThemeContextProviderProps = {
  children: JSX.Element;
};

type ThemeContextState = {
  web3dSettings: Accessor<Web3dSettings>;
  setWeb3dSettings: Setter<Web3dSettings>;
  themeSettings: Accessor<ThemeSettings>;
  setThemeSettings: Setter<ThemeSettings>;
  preferenceSettings: Accessor<ViewerPreferenceSettings>;
  setPreferenceSettings: Setter<ViewerPreferenceSettings>;
  pointBudget: Accessor<number>;
  setPointBudget(value: number): void;
  currentTheme: Accessor<Theme>;
  setTheme: Setter<Theme>;
};

const ThemeContext = createContext<ThemeContextState>();

export function ThemeContextProvider(
  props: Readonly<ThemeContextProviderProps>
): JSX.Element {
  const [web3dSettings, setWeb3dSettings] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<Web3dSettings>({
      // 0m opens pano/planar panes with the cloud hidden: the slider
      // drives ImagePointcloudCapability.activate/deactivate, so
      // 0 → PC provider unregistered → no Three.js traversal / draws.
      imageViewPointcloudClippingDistance: 0,
      imageViewPreRenderStationCount: 5,
    }),
    {
      name: PERSISTENCE_KEY.Web3dSettings,
    }
  );

  const [themeSettings, setThemeSettings] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<ThemeSettings>({
      darkModeMaterials: false,
      backgroundTopColor: '#f1f1f6',
      backgroundBottomColor: '#e0e0e0',
      textColor: '#252a2e',
    }),
    {
      name: PERSISTENCE_KEY.ThemeSettings,
    }
  );

  // Separate from themeSettings so the Modus theme can't clobber the user's choice;
  // PaneViewer reads this as the source of truth for the viewer background.
  const [preferenceSettings, setPreferenceSettings] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<ViewerPreferenceSettings>({
      ...DEFAULT_VIEWER_PREFERENCE_SETTINGS,
    }),
    {
      name: PERSISTENCE_KEY.ViewerPreferenceSettings,
      validate: isValidViewerPreferenceSettings,
    }
  );

  const [storedPointBudget, setStoredPointBudget] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<number | undefined>(undefined),
    {
      name: PERSISTENCE_KEY.PointBudget,
      deserialize: parsePointBudget,
    }
  );
  // Drops a stored value that failed validation so it isn't re-read on every load.
  if (storedPointBudget() === undefined) {
    setStoredPointBudget(undefined);
  }

  const pointBudget = (): number => storedPointBudget() ?? DEFAULT_POINT_BUDGET;

  const setPointBudget = (value: number): void => {
    if (isValidPointBudget(value)) {
      setStoredPointBudget(value);
    }
  };

  const getModusTheme = (): IThemeConfig => {
    const themeConfig = localStorage.getItem('modus-theme-config');
    if (themeConfig) {
      try {
        const parsed = JSON.parse(themeConfig) as IThemeConfig;
        if ('mode' in parsed && 'theme' in parsed) {
          return parsed;
        }
      } catch {
        // If parsing fails, fall through to return default
      }
    }
    // Set modus light as default, otherwise system color would
    // be set and dark is not yet officially supported.
    return { mode: 'light', theme: 'modus-classic' };
  };

  const modusTheme = getModusTheme();
  // Modus handles theme persistence, but we need this to sync Web3D viewer colors when theme changes
  const [currentTheme, setTheme] = createSignal<Theme>(modusTheme.mode);

  createEffect(() => {
    const theme = currentTheme();
    const newThemeSettings = getWeb3dSettingsForTheme(theme);
    // Avoid eager reading of themeSettings() here to prevent creating
    // unnecessary reactive dependencies that may loop in tests.
    setThemeSettings((prev) => ({
      ...prev,
      ...newThemeSettings,
    }));
  });

  return (
    <ThemeContext.Provider
      value={{
        web3dSettings,
        setWeb3dSettings,
        themeSettings,
        setThemeSettings,
        preferenceSettings,
        setPreferenceSettings,
        pointBudget,
        setPointBudget,
        currentTheme,
        setTheme,
      }}
    >
      <modus-wc-theme-provider initial-theme={modusTheme}>
        {props.children}
      </modus-wc-theme-provider>
    </ThemeContext.Provider>
  );
}

export const useThemeContext = (): ThemeContextState => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error(
      'useThemeContext must be used within a ThemeContextProvider'
    );
  }

  return context;
};
